/**
 * Reporting policy for events sent to Sentry.
 *
 * Every event flowing through `beforeSend` is first assigned a category by
 * `classifyEvent`, then routed through the policy below. Changing what we
 * report (or how aggressively) should mean editing this file and nothing else.
 *
 * Rate-limited categories ("sample"):
 * - Always send the first event seen per Sentry fingerprint per tab session
 *   (so a genuinely novel issue is never dropped on first occurrence).
 * - Subsequent events with the same fingerprint are sent with probability
 *   `rate` (uniform sampling).
 * - In-memory only — state resets on reload / new tab. We accept that each
 *   long-running session will surface one early sample per fingerprint, in
 *   exchange for zero persistence overhead.
 */

export type EventCategory =
  | 'csp-violation' // ReportingObserver CSP reports
  | 'decoding-error' // api-client DecodingError (Zod schema drift)
  | 'api-4xx' // HttpError/AjaxError/AxiosError 400 — may be a FE bug
  | 'api-4xx-other' // non-400 4xx (401/403/404/422/…) — user/permission-driven, drop
  | 'api-4xx-noise' // known-benign 400s (auth/oauth, search "Invalid hyperlink")
  | 'api-5xx' // 5xx — never a FE bug, drop
  | 'api-network' // RequestError, ECONNABORTED, chunk-reload "Failed to fetch"
  | 'info-noise' // residual info-level captureMessage calls we still emit
  | 'default'; // everything else — passes through

export type Policy =
  | { type: 'drop' }
  | { type: 'sample'; rate: number; description: string }
  | { type: 'pass' };

export const POLICIES: Record<EventCategory, Policy> = {
  // Pure noise — drop at the SDK boundary.
  'info-noise': { type: 'drop' },

  // Backend-side problem; the user can't act on this from the FE and we have
  // BE alerting already.
  'api-5xx': { type: 'drop' },

  // Network failures (offline, cancelled navigation, chunk reload during
  // deploy). FE has no remediation path; rely on BE/infra signals instead.
  'api-network': { type: 'drop' },

  // ~5k events/day across ~54k users. Sample at 1% to keep visibility into
  // CSP drift without paying for every report. Volume after sampling: ~50/day.
  'csp-violation': {
    type: 'sample',
    rate: 0.01,
    description:
      'CSP violations are high-volume but useful for spotting policy drift. 1% sample + guaranteed first-per-fingerprint preserves signal at ~50 events/day.',
  },

  // Zod schema drift between FE and BE. Real signal but can flood when a
  // schema changes. 10% keeps representative samples per fingerprint.
  'decoding-error': {
    type: 'sample',
    rate: 0.1,
    description:
      'DecodingError indicates real BE/FE contract drift. 10% sample + guaranteed first-per-fingerprint catches every new shape immediately.',
  },

  // 400 may indicate a FE bug (sent malformed payload, stale UI). Keep.
  // Matches develop's whitelist of `loggedApiErrorCodes = [400]`.
  'api-4xx': { type: 'pass' },

  // All other 4xx (401/403/404/422/429/…) are user- or permission-driven and
  // not actionable from the FE. Develop never reported these. Drop.
  'api-4xx-other': { type: 'drop' },

  // Known-benign 4xx patterns we previously suppressed explicitly:
  //   - 400s on /auth/ and /oauth/ (failed logins, expired refresh tokens)
  //   - /annotations/search 400 "Invalid hyperlink - Object does not exist."
  //     (stale reference; user just retries the search)
  // Neither is actionable from the FE. Drop entirely.
  'api-4xx-noise': { type: 'drop' },

  // Unhandled / genuinely-novel errors. Always send.
  default: { type: 'pass' },
};
